import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { SEO } from '@/components/SEO';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { parseFlexibleDate, parseTallyDate } from '@/lib/dateUtils';
import { errorLogger } from '@/lib/errorLogger';
import { 
  CheckCircle, ArrowRight, ArrowLeft, Upload, RefreshCw, ShieldAlert, 
  FileSpreadsheet, Building2, Sparkles, Target, BarChart3, Loader2,
  FileCheck, AlertTriangle, TrendingUp
} from 'lucide-react';

interface BankTransaction {
  date: string;
  narration: string;
  amount: number;
}

interface TallyEntry {
  date: string;
  ledger: string;
  narration: string;
  amount: number;
  voucher_type?: string;
}

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { incrementTransactions } = useUsageTracking();
  const bankFileRef = useRef<HTMLInputElement>(null);
  const tallyFileRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bankData, setBankData] = useState<BankTransaction[]>([]);
  const [tallyData, setTallyData] = useState<TallyEntry[]>([]);
  const [bankFileName, setBankFileName] = useState('');
  const [tallyFileName, setTallyFileName] = useState('');
  const [importedBank, setImportedBank] = useState(false);
  const [importedTally, setImportedTally] = useState(false);

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const parseCSV = (text: string): BankTransaction[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    
    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      const rawDate = cols[0] || '';
      const parsedDate = parseFlexibleDate(rawDate);
      
      return {
        date: parsedDate,
        narration: (cols[1] || 'Unknown').substring(0, 500),
        amount: parseFloat((cols[2] || '0').replace(/[^0-9.-]/g, '')) || 0
      };
    }).filter(t => t.narration && t.amount !== 0);
  };

  const parseTallyXML = (xmlText: string): TallyEntry[] => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    const vouchers = xmlDoc.getElementsByTagName('VOUCHER');
    const entries: TallyEntry[] = [];

    for (let i = 0; i < vouchers.length; i++) {
      const voucher = vouchers[i];
      const dateEl = voucher.getElementsByTagName('DATE')[0];
      const ledgerEl = voucher.getElementsByTagName('PARTYLEDGERNAME')[0] || voucher.getElementsByTagName('LEDGERNAME')[0];
      const narrationEl = voucher.getElementsByTagName('NARRATION')[0];
      const amountEl = voucher.getElementsByTagName('AMOUNT')[0];
      const voucherType = voucher.getAttribute('VCHTYPE') || 'Payment';

      if (amountEl) {
        const rawDate = dateEl?.textContent || '';
        const formattedDate = rawDate.length === 8 
          ? parseTallyDate(rawDate)
          : parseFlexibleDate(rawDate);

        const amount = Math.abs(parseFloat(amountEl.textContent || '0'));
        if (amount > 0) {
          entries.push({
            date: formattedDate,
            ledger: (ledgerEl?.textContent || 'Unknown').substring(0, 200),
            narration: (narrationEl?.textContent || ledgerEl?.textContent || 'Unknown').substring(0, 500),
            amount,
            voucher_type: voucherType
          });
        }
      }
    }

    return entries;
  };

  const handleBankUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBankFileName(file.name);
    setLoading(true);

    try {
      const text = await file.text();
      const transactions = parseCSV(text);
      
      if (transactions.length === 0) {
        throw new Error('No valid transactions found. Ensure CSV has Date, Narration, Amount columns.');
      }

      setBankData(transactions);
      toast({ title: `Parsed ${transactions.length} transactions`, description: 'Ready to import' });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Parse Error', description: error.message });
      setBankFileName('');
    } finally {
      setLoading(false);
    }
  };

  const handleTallyUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setTallyFileName(file.name);
    setLoading(true);

    try {
      const text = await file.text();
      const entries = parseTallyXML(text);
      
      if (entries.length === 0) {
        throw new Error('No valid entries found. Ensure file is a valid Tally XML export.');
      }

      setTallyData(entries);
      toast({ title: `Parsed ${entries.length} entries`, description: 'Ready to import' });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Parse Error', description: error.message });
      setTallyFileName('');
    } finally {
      setLoading(false);
    }
  };

  const importBankData = async () => {
    if (!user || bankData.length === 0) return;
    setLoading(true);

    try {
      const { error } = await supabase.from('bank_transactions').insert(
        bankData.map(t => ({
          user_id: user.id,
          date: t.date,
          narration: t.narration,
          amount: t.amount,
          status: 'unmatched'
        }))
      );

      if (error) {
        errorLogger.trackImport('csv', false, bankData.length, error.message);
        throw error;
      }
      
      // Track usage
      await incrementTransactions(bankData.length);
      errorLogger.trackImport('csv', true, bankData.length);
      
      setImportedBank(true);
      toast({ title: 'Success', description: `Imported ${bankData.length} bank transactions` });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Import Failed', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const importTallyData = async () => {
    if (!user || tallyData.length === 0) return;
    setLoading(true);

    try {
      const { error } = await supabase.from('tally_entries').insert(
        tallyData.map(e => ({
          user_id: user.id,
          date: e.date,
          ledger: e.ledger,
          narration: e.narration,
          amount: e.amount,
          voucher_type: e.voucher_type,
          status: 'unmatched'
        }))
      );

      if (error) {
        errorLogger.trackImport('xml', false, tallyData.length, error.message);
        throw error;
      }
      
      errorLogger.trackImport('xml', true, tallyData.length);
      setImportedTally(true);
      toast({ title: 'Success', description: `Imported ${tallyData.length} Tally entries` });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Import Failed', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 2: return bankData.length > 0;
      case 3: return tallyData.length > 0;
      default: return true;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-primary-foreground" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground pb-1">Welcome to Sentinel</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                Your autonomous financial command center. Let's get you set up in just a few minutes.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-border/50 p-4 text-center">
                <RefreshCw className="h-8 w-8 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold text-foreground pb-1">Automatic Reconciliation</h3>
                <p className="text-xs text-muted-foreground">Matches bank & ledger entries automatically</p>
              </Card>
              <Card className="border-border/50 p-4 text-center">
                <ShieldAlert className="h-8 w-8 mx-auto mb-3 text-destructive" />
                <h3 className="font-semibold text-foreground pb-1">Real-Time Fraud Detection</h3>
                <p className="text-xs text-muted-foreground">Identifies duplicate invoices & anomalies</p>
              </Card>
              <Card className="border-border/50 p-4 text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-3 text-success" />
                <h3 className="font-semibold text-foreground pb-1">Cash Flow Forecasting</h3>
                <p className="text-xs text-muted-foreground">Predicts your runway with precision</p>
              </Card>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <FileSpreadsheet className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground pb-1">Import Bank Statement</h2>
                  <p className="text-sm text-muted-foreground">Upload your bank transactions as CSV</p>
                </div>
              </div>
            </div>

            <Card className="border-2 border-dashed border-border p-6">
              <input
                ref={bankFileRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleBankUpload}
              />
              
              {!bankFileName ? (
                <div 
                  className="text-center cursor-pointer py-8"
                  onClick={() => bankFileRef.current?.click()}
                >
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Upload Bank CSV</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    File should have: Date, Narration/Description, Amount columns
                  </p>
                  <Button variant="outline">Select File</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-success/10 rounded-lg">
                    <FileCheck className="h-5 w-5 text-success" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate">{bankFileName}</div>
                      <div className="text-sm text-muted-foreground">{bankData.length} transactions found</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setBankFileName(''); setBankData([]); }}>
                      Change
                    </Button>
                  </div>

                  {bankData.length > 0 && !importedBank && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-foreground">Preview (first 3):</h4>
                      <div className="space-y-2 text-sm">
                        {bankData.slice(0, 3).map((t, i) => (
                          <div key={i} className="flex justify-between p-2 bg-muted/50 rounded">
                            <span className="truncate flex-1 text-muted-foreground">{t.narration}</span>
                            <span className={t.amount > 0 ? 'text-success' : 'text-destructive'}>
                              ₹{Math.abs(t.amount).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Button onClick={importBankData} disabled={loading} className="w-full">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Import {bankData.length} Transactions
                      </Button>
                    </div>
                  )}

                  {importedBank && (
                    <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg text-success">
                      <CheckCircle className="h-5 w-5" />
                      <span>Successfully imported!</span>
                    </div>
                  )}
                </div>
              )}
            </Card>

            <div className="p-4 bg-muted/30 rounded-lg">
              <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                CSV Format Tips
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• First row should be headers</li>
                <li>• Date format: YYYY-MM-DD or DD/MM/YYYY</li>
                <li>• Amount can include currency symbols</li>
              </ul>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground pb-1">Import Tally Data</h2>
                  <p className="text-sm text-muted-foreground">Upload your Tally voucher export (XML)</p>
                </div>
              </div>
            </div>

            <Card className="border-2 border-dashed border-border p-6">
              <input
                ref={tallyFileRef}
                type="file"
                accept=".xml"
                className="hidden"
                onChange={handleTallyUpload}
              />
              
              {!tallyFileName ? (
                <div 
                  className="text-center cursor-pointer py-8"
                  onClick={() => tallyFileRef.current?.click()}
                >
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Upload Tally XML</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Export vouchers from Tally ERP in XML format
                  </p>
                  <Button variant="outline">Select File</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-success/10 rounded-lg">
                    <FileCheck className="h-5 w-5 text-success" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate">{tallyFileName}</div>
                      <div className="text-sm text-muted-foreground">{tallyData.length} entries found</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setTallyFileName(''); setTallyData([]); }}>
                      Change
                    </Button>
                  </div>

                  {tallyData.length > 0 && !importedTally && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-foreground">Preview (first 3):</h4>
                      <div className="space-y-2 text-sm">
                        {tallyData.slice(0, 3).map((e, i) => (
                          <div key={i} className="flex justify-between p-2 bg-muted/50 rounded">
                            <span className="truncate flex-1 text-muted-foreground">{e.ledger}</span>
                            <span className="text-foreground">₹{e.amount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                      <Button onClick={importTallyData} disabled={loading} className="w-full">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Import {tallyData.length} Entries
                      </Button>
                    </div>
                  )}

                  {importedTally && (
                    <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg text-success">
                      <CheckCircle className="h-5 w-5" />
                      <span>Successfully imported!</span>
                    </div>
                  )}
                </div>
              )}
            </Card>

            <div className="p-4 bg-muted/30 rounded-lg">
              <h4 className="font-medium text-foreground mb-2">How to export from Tally:</h4>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Go to Gateway of Tally → Display → Day Book</li>
                <li>Press Alt+E to export</li>
                <li>Select XML format and save</li>
              </ol>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground pb-1">What's Next?</h2>
                  <p className="text-sm text-muted-foreground">Here's what you can do with Sentinel Pro</p>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <Card className="border-border/50 p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <RefreshCw className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground pb-1">SYNC Engine</h3>
                    <p className="text-sm text-muted-foreground">
                      Our AI will automatically match your bank transactions with Tally entries. 
                      Review suggested matches and confirm with one click.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="border-border/50 p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-destructive/10">
                    <ShieldAlert className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground pb-1">WATCH Engine</h3>
                    <p className="text-sm text-muted-foreground">
                      Upload invoices and our AI analyzes them for duplicates and fraud. 
                      Get instant risk scores and alerts for suspicious activity.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="border-border/50 p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-success/10">
                    <BarChart3 className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground pb-1">FORESIGHT Engine</h3>
                    <p className="text-sm text-muted-foreground">
                      See 30-day cash flow projections powered by AI. 
                      Understand your financial runway and plan ahead.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6 text-center">
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-success to-success/60 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-success-foreground" />
            </div>
            
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground pb-2">You're All Set!</h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                Your data is imported and ready. Start exploring Sentinel Pro's powerful features.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3 pt-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2"
                onClick={() => navigate('/sync')}
              >
                <RefreshCw className="h-5 w-5" />
                <span>Go to SYNC</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2"
                onClick={() => navigate('/watch')}
              >
                <ShieldAlert className="h-5 w-5" />
                <span>Go to WATCH</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2"
                onClick={() => navigate('/foresight')}
              >
                <BarChart3 className="h-5 w-5" />
                <span>Go to FORESIGHT</span>
              </Button>
            </div>

            <Button 
              size="lg" 
              className="w-full mt-4"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Get Started - Sentinel Pro" 
        description="Set up your AI-powered financial reconciliation platform in minutes" 
      />
      
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">
              Step {step} of {totalSteps}
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Content Card */}
        <Card className="border-border/50">
          <CardContent className="p-6 md:p-8">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {step < totalSteps && (
            <Button 
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            >
              {step === 2 && !importedBank && bankData.length > 0 ? 'Skip' : 
               step === 3 && !importedTally && tallyData.length > 0 ? 'Skip' : 'Next'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
